import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { SocketProvider } from './context/SocketContext';
import { HelmetProvider } from 'react-helmet-async';
import { ToastProvider, ErrorBoundary, Spinner } from './components/ui';
import Header from './components/Header';
import Footer from './components/Footer';
import { useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
// Customer-facing core pages — eager (homepage critical-path)
import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import EnhancedCheckout from './pages/EnhancedCheckout';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import Account from './pages/Account';
import QuickOrder from './pages/QuickOrder';
import MassEntry from './pages/MassEntry';
import NotificationsSettings from './pages/NotificationsSettings';
import Apply from './pages/Apply';
import ApprovalBanner from './components/ApprovalBanner';
// Admin login — eager so the keyboard shortcut hop is instant
import AdminLogin from './pages/admin/Login';
import AdminLayout from './components/admin/AdminLayout';
import LiveEventsToast from './components/admin/LiveEventsToast';
import CartDrawer from './components/CartDrawer';
import { DialogProvider } from './components/admin/Dialog';
import ChatbotWidget from './components/ChatbotWidget';
import CookieConsent from './components/CookieConsent';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import ExitIntentModal from './components/ExitIntentModal';
import ThemeProvider from './components/ThemeProvider';
import PageRenderer from './components/PageRenderer';
import SEO from './components/SEO';
import LegalReacceptBanner from './components/LegalReacceptBanner';
import StagingBanner from './components/StagingBanner';
import { useCart } from './context/CartContext';
import './App.css';
import './pages/PageShared.css';
import GradeCard from './pages/GradeCard';
import GradeAccuracy from './pages/GradeAccuracy';

function BuilderPage({ slug, title }) {
  return (
    <div className="page" style={{ minHeight: '60vh' }}>
      <SEO title={title} />
      <PageRenderer slug={slug} fallback={
        <div style={{ padding: '64px 16px', textAlign: 'center', color: '#888' }}>
          <h1>{title}</h1>
          <p>This page is being built. Check back soon.</p>
        </div>
      } />
    </div>
  );
}

// Helper: lazy-load a named export from a multi-export module
const lazyNamed = (factory, name) => lazy(() => factory().then(m => ({ default: m[name] })));

// Customer-facing secondary pages — lazy
const CartRecovery   = lazy(() => import('./pages/CartRecovery'));
const Checkout       = lazy(() => import('./pages/Checkout'));
const ComingSoon     = lazy(() => import('./pages/ComingSoon'));
const Auctions       = lazy(() => import('./pages/Auctions'));
const AuctionDetail  = lazy(() => import('./pages/AuctionDetail'));
const PreOrders      = lazy(() => import('./pages/PreOrders'));
const TradeIns       = lazy(() => import('./pages/TradeIns'));
const SavedSearches  = lazy(() => import('./pages/SavedSearches'));
const Sell           = lazy(() => import('./pages/Sell'));
const TrackOrder     = lazy(() => import('./pages/TrackOrder'));
const Subscribe      = lazy(() => import('./pages/Subscribe'));
const LiveStream     = lazy(() => import('./pages/LiveStream'));
const TrophyRoom     = lazy(() => import('./pages/TrophyRoom'));
const WishlistShare  = lazy(() => import('./pages/WishlistShare'));
const QueueAlerts    = lazy(() => import('./pages/QueueAlerts'));
const MyCollection   = lazy(() => import('./pages/MyCollection'));
const BlogIndex   = lazyNamed(() => import('./pages/Blog'),   'BlogIndex');
const BlogPost    = lazyNamed(() => import('./pages/Blog'),   'BlogPost');
const EventsIndex = lazyNamed(() => import('./pages/Events'), 'EventsIndex');
const EventDetail = lazyNamed(() => import('./pages/Events'), 'EventDetail');
const DecksIndex  = lazyNamed(() => import('./pages/Decks'),  'DecksIndex');
const DeckEditor  = lazyNamed(() => import('./pages/Decks'),  'DeckEditor');
const Privacy     = lazyNamed(() => import('./pages/Legal'),  'Privacy');
const Terms       = lazyNamed(() => import('./pages/Legal'),  'Terms');

// Admin pages — all lazy (saves ~1.2MB from main bundle)
const AdminDashboard       = lazy(() => import('./pages/admin/Dashboard'));
const AdminProducts        = lazy(() => import('./pages/admin/Products'));
const AdminSites           = lazy(() => import('./pages/admin/Sites'));
const AdminOrders          = lazy(() => import('./pages/admin/Orders'));
const AdminBulkImport      = lazy(() => import('./pages/admin/BulkImport'));
const AdminCatalogMaint    = lazy(() => import('./pages/admin/CatalogMaintenance'));
const AdminScanMode        = lazy(() => import('./pages/admin/ScanMode'));
const AdminBuylist         = lazy(() => import('./pages/admin/Buylist'));
const AdminPricingRules    = lazy(() => import('./pages/admin/PricingRules'));
const AdminProductDetail   = lazy(() => import('./pages/admin/ProductDetail'));
const AdminAuditLog        = lazy(() => import('./pages/admin/AuditLog'));
const AdminReviews         = lazy(() => import('./pages/admin/Reviews'));
const AdminReceiving       = lazy(() => import('./pages/admin/Receiving'));
const AdminCycleCount      = lazy(() => import('./pages/admin/CycleCount'));
const AdminWholesale       = lazy(() => import('./pages/admin/Wholesale'));
const AdminErrorLog        = lazy(() => import('./pages/admin/ErrorLog'));
const AdminAccounting      = lazy(() => import('./pages/admin/Accounting'));
const AdminDrops           = lazy(() => import('./pages/admin/Drops'));
const AdminNewsletter      = lazy(() => import('./pages/admin/Newsletter'));
const AdminPosts           = lazy(() => import('./pages/admin/Posts'));
const AdminSiteSettings    = lazy(() => import('./pages/admin/SiteSettings'));
const AdminBanners         = lazy(() => import('./pages/admin/Banners'));
const AdminChatbot         = lazy(() => import('./pages/admin/Chatbot'));
const AdminEmailLog        = lazy(() => import('./pages/admin/EmailLog'));
const AdminJobs            = lazy(() => import('./pages/admin/JobsInspector'));
const AdminWebhooks        = lazy(() => import('./pages/admin/WebhookEvents'));
const AdminZohoMapping     = lazy(() => import('./pages/admin/ZohoMapping'));
const AdminZohoSyncLog     = lazy(() => import('./pages/admin/ZohoSyncLog'));
const AdminPageBuilder     = lazy(() => import('./pages/admin/PageBuilder'));
const AdminIntegrations    = lazy(() => import('./pages/admin/Integrations'));
const AdminEmailPreview    = lazy(() => import('./pages/admin/EmailPreview'));
const AdminLaunchReadiness = lazy(() => import('./pages/admin/LaunchReadiness'));
const AdminZohoSetup       = lazy(() => import('./pages/admin/ZohoSetup'));
const AdminSubscriptions   = lazy(() => import('./pages/admin/Subscriptions'));
const AdminQueueAlerts     = lazy(() => import('./pages/admin/QueueAlerts'));
const AdminHelpNotifications = lazy(() => import('./pages/admin/HelpNotifications'));
const AdminSetsIndex          = lazy(() => import('./pages/admin/SetsIndex'));
const AdminSetInventory       = lazy(() => import('./pages/admin/SetInventory'));
const AdminConditionRules     = lazy(() => import('./pages/admin/ConditionRules'));
const SetCardBrowser          = lazy(() => import('./pages/SetCardBrowser'));
const AdminPWA             = lazy(() => import('./pages/admin/PWA'));
const AdminSeoContent      = lazy(() => import('./pages/admin/SeoContent'));
const AlertsLandingPage    = lazy(() => import('./pages/AlertsLanding').then(m => ({ default: m.AlertsLanding })));
const AlertsConfirmPage    = lazy(() => import('./pages/AlertsLanding').then(m => ({ default: m.AlertsConfirm })));
const AdminAlerts          = lazy(() => import('./pages/admin/Alerts'));
const AdminBundles         = lazy(() => import('./pages/admin/Bundles'));
const AdminExitIntent      = lazy(() => import('./pages/admin/ExitIntent'));
const AdminCollections     = lazy(() => import('./pages/admin/Collections'));
const AdminApplications    = lazy(() => import('./pages/admin/Applications'));
const AdminQuotes          = lazy(() => import('./pages/admin/Quotes'));
const AdminAutomationFlows = lazy(() => import('./pages/admin/AutomationFlows'));
const AdminReferrals       = lazy(() => import('./pages/admin/Referrals'));
const AdminFunnels         = lazy(() => import('./pages/admin/Funnels'));
const MyQuotes             = lazy(() => import('./pages/Quotes'));
const SezzleReturnPage     = lazy(() => import('./pages/SezzleReturn').then(m => ({ default: m.SezzleReturn })));
const SezzleCancelPage     = lazy(() => import('./pages/SezzleReturn').then(m => ({ default: m.SezzleCancel })));
const AdminHelcim          = lazy(() => import('./pages/admin/Helcim'));
const AdminCategories      = lazy(() => import('./pages/admin/Categories'));
const AdminPressGallery    = lazy(() => import('./pages/admin/PressGallery'));
const AdminPurchaseLimits  = lazy(() => import('./pages/admin/PurchaseLimits'));
const AdminMarkupRules     = lazy(() => import('./pages/admin/MarkupRules'));
const AdminFlyers          = lazy(() => import('./pages/admin/Flyers'));
const Collection           = lazy(() => import('./pages/Collection'));
const SeoSets              = lazy(() => import('./pages/seo/Sets'));
const SeoSetDetail         = lazy(() => import('./pages/seo/SetDetail'));
const SeoCardDetail        = lazy(() => import('./pages/seo/CardDetail'));
const SeoDrops             = lazy(() => import('./pages/seo/Drops').then(m => ({ default: m.Drops })));
const SeoDropDetail        = lazy(() => import('./pages/seo/Drops').then(m => ({ default: m.DropDetail })));
const AdminCustomers       = lazy(() => import('./pages/admin/Customers'));
const AdminCustomerGroups  = lazy(() => import('./pages/admin/CustomerGroups'));
const AdminPriceLists      = lazy(() => import('./pages/admin/PriceLists'));
const AdminAR              = lazy(() => import('./pages/admin/AccountsReceivable'));
const AdminChannelTcg      = lazy(() => import('./pages/admin/ChannelTcgplayer'));
const AdminChannelEbay     = lazy(() => import('./pages/admin/ChannelEbay'));
const AdminChannelAmazon   = lazy(() => import('./pages/admin/ChannelAmazon'));
const AdminChannelTiktok   = lazy(() => import('./pages/admin/ChannelTiktok'));
const AdminApiTokens       = lazy(() => import('./pages/admin/ApiTokens'));
const AdminWarehouses      = lazy(() => import('./pages/admin/Warehouses'));
const AdminRoutingRules    = lazy(() => import('./pages/admin/RoutingRules'));
const AdminPOS             = lazy(() => import('./pages/admin/POS'));
const AdminEnrichment      = lazy(() => import('./pages/admin/EnrichmentQueue'));
const AdminTeam            = lazy(() => import('./pages/admin/Team'));
const AdminInventory       = lazy(() => import('./pages/admin/Inventory'));
const AdminReports         = lazy(() => import('./pages/admin/Reports'));
const AdminReturns         = lazy(() => import('./pages/admin/Returns'));
const AdminCustomerDetail  = lazy(() => import('./pages/admin/CustomerDetail'));
const AdminVariantMatrix   = lazy(() => import('./pages/admin/VariantMatrix'));
const AdminOrderDetail     = lazy(() => import('./pages/admin/OrderDetail'));
const AdminSearchAnalytics = lazy(() => import('./pages/admin/SearchAnalytics'));
const AdminFeatureFlags    = lazy(() => import('./pages/admin/FeatureFlags'));
const AdminSeo             = lazy(() => import('./pages/admin/Seo'));
const AdminCoupons         = lazy(() => import('./pages/admin/Coupons'));
const AdminLoyalty         = lazy(() => import('./pages/admin/Loyalty'));
const AdminGrading         = lazy(() => import('./pages/admin/Grading'));
const AdminEmailRoles      = lazy(() => import('./pages/admin/EmailRoles'));
const AdminAutomations     = lazy(() => import('./pages/admin/Automations'));
const AdminVendors         = lazy(() => import('./pages/admin/Vendors'));
const AdminWriteOffs       = lazy(() => import('./pages/admin/WriteOffs'));
const AdminStockForecast   = lazy(() => import('./pages/admin/StockForecast'));
const AdminInvoicing       = lazy(() => import('./pages/admin/Invoicing'));

function CartDrawerMount() {
  const { drawerOpen, closeDrawer } = useCart();
  return <CartDrawer open={drawerOpen} onClose={closeDrawer} />;
}

// Hidden admin shortcut: type "g" then "a" within 1.5s on any public page → /admin/login.
// Replaces the visible "Admin" link in Header; reduces credential-stuffing reconnaissance.
function useAdminKeyboardShortcut() {
  const navigate = useNavigate();
  React.useEffect(() => {
    let lastKey = '', lastTime = 0;
    const handler = (e) => {
      // Skip if user is typing in a form
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const now = Date.now();
      if (e.key === 'g') { lastKey = 'g'; lastTime = now; }
      else if (e.key === 'a' && lastKey === 'g' && (now - lastTime) < 1500) {
        navigate('/admin/login');
        lastKey = ''; lastTime = 0;
      } else { lastKey = ''; lastTime = 0; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);
}

// Hide the storefront chrome on /admin/* — AdminLayout provides its own full-bleed shell.
function StorefrontChrome({ children }) {
  const loc = useLocation();
  const isAdmin = loc.pathname.startsWith('/admin');
  const qs = new URLSearchParams(loc.search);
  const isBuilderPreview = qs.get('builder') === '1' && qs.get('overlays') !== '1';
  useAdminKeyboardShortcut();
  return (
    <>
      <ThemeProvider />
      {!isAdmin && <Header />}
      {!isAdmin && <ApprovalBanner />}
      {!isAdmin && <LegalReacceptBanner />}
      {children}
      {!isAdmin && <Footer />}
      {!isAdmin && <CartDrawerMount />}
      {!isAdmin && <LiveEventsToast />}
      {!isAdmin && !isBuilderPreview && <ChatbotWidget />}
      {!isAdmin && <CookieConsent />}
      {!isAdmin && <PWAInstallPrompt />}
      {!isAdmin && <ExitIntentModal />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <HelmetProvider>
    <ToastProvider>
    <CurrencyProvider>
    <CartProvider>
    <SocketProvider>
    <DialogProvider>
      <Router>
        <div className="App">
          <StagingBanner />
          <a href="#main-content" className="skip-link">
            Skip to main content
          </a>
          <StorefrontChrome>
          <main id="main-content" className="main-content" role="main">
            <Suspense fallback={<div style={{ padding: 48, textAlign: 'center' }}><Spinner /></div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              {/* Phase 4: nested taxonomy URLs. Same Shop component handles
                  the flat /shop/:category (legacy) and the 1-4 deep nested
                  /shop/:game/:era/:series/:set form. Shop reads the trailing
                  segment as the active category. */}
              <Route path="/shop/:category" element={<Shop />} />
              <Route path="/shop/:game/:era" element={<Shop />} />
              <Route path="/shop/:game/:era/:series" element={<Shop />} />
              <Route path="/shop/:game/:era/:series/:set" element={<Shop />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/cart/recover/:token" element={<CartRecovery />} />
              <Route path="/quick-order" element={<QuickOrder />} />
              <Route path="/mass-entry" element={<MassEntry />} />
              <Route path="/checkout" element={<EnhancedCheckout />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ResetPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/register" element={<Register />} />
              <Route path="/account" element={<Account />} />
              <Route path="/account/notifications" element={<NotificationsSettings />} />
              <Route path="/notifications" element={<NotificationsSettings />} />
              <Route path="/sets"            element={<SeoSets />} />
              <Route path="/sets/:slug"      element={<SeoSetDetail />} />
              <Route path="/cards/:slug"     element={<SeoCardDetail />} />
              <Route path="/drops"           element={<SeoDrops />} />
              <Route path="/drops/:slug"     element={<SeoDropDetail />} />
              <Route path="/alerts"          element={<AlertsLandingPage />} />
              <Route path="/alerts/confirm/:token" element={<AlertsConfirmPage />} />
              <Route path="/collection"      element={<Collection />} />
              <Route path="/apply"           element={<Apply />} />
              <Route path="/quotes"          element={<MyQuotes />} />
              <Route path="/checkout/sezzle/return" element={<SezzleReturnPage />} />
              <Route path="/checkout/sezzle/cancel" element={<SezzleCancelPage />} />
              <Route path="/account/*" element={<Account />} />
              <Route path="/coming-soon" element={<ComingSoon />} />

              {/* AI Card Grader (Phase 1: centering pre-grade) */}
              <Route path="/grade" element={<GradeCard />} />
              <Route path="/grade/accuracy" element={<GradeAccuracy />} />
              <Route path="/grade/share/:token" element={<GradeCard />} />

              {/* Phase 8 routes */}
              <Route path="/auctions" element={<Auctions />} />
              <Route path="/auctions/:id" element={<AuctionDetail />} />
              <Route path="/pre-orders" element={<PreOrders />} />
              <Route path="/trade-ins" element={
                <ProtectedRoute>
                  <TradeIns />
                </ProtectedRoute>
              } />
              <Route path="/saved-searches" element={
                <ProtectedRoute>
                  <SavedSearches />
                </ProtectedRoute>
              } />

              {/* Admin routes — login is bare; everything else gets the sidebar layout */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="sites" element={<AdminSites />} />
              <Route path="products/:id" element={<AdminProductDetail />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="import" element={<AdminBulkImport />} />
              <Route path="catalog/maintenance" element={<AdminCatalogMaint />} />
              <Route path="scan" element={<AdminScanMode />} />
              <Route path="buylist" element={<AdminBuylist />} />
              <Route path="pricing" element={<AdminPricingRules />} />
              <Route path="audit"     element={<AdminAuditLog />} />
              <Route path="reviews"   element={<AdminReviews />} />
              <Route path="receiving" element={<AdminReceiving />} />
              <Route path="counts"    element={<AdminCycleCount />} />
              <Route path="wholesale" element={<AdminWholesale />} />
              <Route path="errors"    element={<AdminErrorLog />} />
              <Route path="accounting" element={<AdminAccounting />} />
              <Route path="drops"      element={<AdminDrops />} />
              <Route path="newsletter" element={<AdminNewsletter />} />
              <Route path="posts"      element={<AdminPosts />} />
              <Route path="site-settings"   element={<AdminSiteSettings />} />
              <Route path="banners"         element={<AdminBanners />} />
              <Route path="chatbot"         element={<AdminChatbot />} />
              <Route path="email-log"       element={<AdminEmailLog />} />
              <Route path="jobs"            element={<AdminJobs />} />
              <Route path="webhooks"        element={<AdminWebhooks />} />
              <Route path="zoho/mapping"    element={<AdminZohoMapping />} />
              <Route path="zoho/sync-log"   element={<AdminZohoSyncLog />} />
              <Route path="builder"         element={<AdminPageBuilder />} />
              <Route path="integrations"    element={<AdminIntegrations />} />
              <Route path="emails"          element={<AdminEmailPreview />} />
              <Route path="launch-readiness" element={<AdminLaunchReadiness />} />
              <Route path="zoho-setup"      element={<AdminZohoSetup />} />
              <Route path="subscriptions"   element={<AdminSubscriptions />} />
              <Route path="queue-alerts"    element={<AdminQueueAlerts />} />
              <Route path="help/notifications" element={<AdminHelpNotifications />} />
              <Route path="sets"                element={<AdminSetsIndex />} />
              <Route path="sets/condition-rules" element={<AdminConditionRules />} />
              <Route path="sets/:game"          element={<AdminSetsIndex />} />
              <Route path="sets/:game/:slug/inventory" element={<AdminSetInventory />} />
              <Route path="pwa"             element={<AdminPWA />} />
              <Route path="seo-content"     element={<AdminSeoContent />} />
              <Route path="alerts"          element={<AdminAlerts />} />
              <Route path="bundles"         element={<AdminBundles />} />
              <Route path="exit-intent"     element={<AdminExitIntent />} />
              <Route path="collections"     element={<AdminCollections />} />
              <Route path="applications"    element={<AdminApplications />} />
              <Route path="quotes"          element={<AdminQuotes />} />
              <Route path="automation-flows" element={<AdminAutomationFlows />} />
              <Route path="referrals"       element={<AdminReferrals />} />
              <Route path="funnels"         element={<AdminFunnels />} />
              <Route path="helcim"          element={<AdminHelcim />} />
              <Route path="categories"      element={<AdminCategories />} />
              <Route path="press-gallery"   element={<AdminPressGallery />} />
              <Route path="purchase-limits" element={<AdminPurchaseLimits />} />
              <Route path="markup-rules"    element={<AdminMarkupRules />} />
              <Route path="flyers"          element={<AdminFlyers />} />
              <Route path="customers"       element={<AdminCustomers />} />
              <Route path="customer-groups" element={<AdminCustomerGroups />} />
              <Route path="price-lists"     element={<AdminPriceLists />} />
              <Route path="ar"              element={<AdminAR />} />
              <Route path="channels/tcgplayer" element={<AdminChannelTcg />} />
              <Route path="channels/ebay"     element={<AdminChannelEbay />} />
              <Route path="channels/amazon"   element={<AdminChannelAmazon />} />
              <Route path="channels/tiktok"   element={<AdminChannelTiktok />} />
              <Route path="api-tokens"        element={<AdminApiTokens />} />
              <Route path="warehouses"      element={<AdminWarehouses />} />
              <Route path="routing-rules"   element={<AdminRoutingRules />} />
              <Route path="pos"             element={<AdminPOS />} />
              <Route path="enrichment-queue" element={<AdminEnrichment />} />
              <Route path="team"            element={<AdminTeam />} />
              <Route path="inventory"       element={<AdminInventory />} />
              <Route path="reports"         element={<AdminReports />} />
              <Route path="returns"         element={<AdminReturns />} />
              <Route path="customers/:id"   element={<AdminCustomerDetail />} />
              <Route path="products/:id/variants" element={<AdminVariantMatrix />} />
              <Route path="orders/:id"      element={<AdminOrderDetail />} />
              <Route path="search-analytics" element={<AdminSearchAnalytics />} />
              <Route path="feature-flags"   element={<AdminFeatureFlags />} />
              <Route path="seo"              element={<AdminSeo />} />
              <Route path="coupons"          element={<AdminCoupons />} />
              <Route path="loyalty"          element={<AdminLoyalty />} />
              <Route path="grading"          element={<AdminGrading />} />
              <Route path="email-roles"      element={<AdminEmailRoles />} />
              <Route path="automations"      element={<AdminAutomations />} />
              <Route path="vendors"          element={<AdminVendors />} />
              <Route path="writeoffs"        element={<AdminWriteOffs />} />
              <Route path="stock-forecast"   element={<AdminStockForecast />} />
              <Route path="invoicing"        element={<AdminInvoicing />} />
              </Route>
              {/* Customer-facing routes that were grouped above */}
              <Route path="/sell"            element={<Sell />} />
              {/* Builder-managed marketing pages — render purely from page_sections.
                  When admin hasn't added sections yet they show a placeholder. */}
              <Route path="/about"           element={<BuilderPage slug="about" title="About" />} />
              <Route path="/contact"         element={<BuilderPage slug="contact" title="Contact" />} />
              <Route path="/track"           element={<TrackOrder />} />
              <Route path="/blog"            element={<BlogIndex />} />
              <Route path="/blog/:slug"      element={<BlogPost />} />
              <Route path="/events"          element={<EventsIndex />} />
              <Route path="/events/:slug"    element={<EventDetail />} />
              <Route path="/decks"           element={<DecksIndex />} />
              <Route path="/decks/:id"       element={<DeckEditor />} />
              <Route path="/collection"      element={<MyCollection />} />
              <Route path="/subscribe"        element={<Subscribe />} />
              <Route path="/live"             element={<LiveStream />} />
              <Route path="/trophy-room"      element={<TrophyRoom />} />
              <Route path="/wishlist/share/:token" element={<WishlistShare />} />
              <Route path="/queue-alerts"          element={<QueueAlerts />} />
              <Route path="/sets/:game/:slug"      element={<SetCardBrowser />} />
              <Route path="/privacy"               element={<Privacy />} />
              <Route path="/terms"                 element={<Terms />} />
            </Routes>
            </Suspense>
          </main>
          </StorefrontChrome>
        </div>
      </Router>
    </DialogProvider>
    </SocketProvider>
    </CartProvider>
    </CurrencyProvider>
    </ToastProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
